'use client';

/**
 * AstorThread — assistant-ui-based chat shell for Astor.
 *
 * Replaces the hand-rolled MessageBubble + ChatMessageBubble surface across
 * all three portals. Uses ThreadPrimitive.Messages so each user/assistant
 * bubble renders inside assistant-ui's message scope (this is what unlocks
 * BranchPicker, ActionBar, edit composer, and per-message tool routing).
 *
 * Tool results are routed through MessagePrimitive.Parts.components.tools
 * via the Phase 2 registry. Markdown text rendering is host-controlled via
 * `renderContent` so the design system stays markdown-dep-free.
 *
 * Issue #1434
 */

import {
  ActionBarPrimitive,
  AssistantRuntimeProvider,
  BranchPickerPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessage,
} from '@assistant-ui/react';
import SparkleIcon from '@mui/icons-material/AutoAwesome';
import CopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import MicIcon from '@mui/icons-material/Mic';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import StopIcon from '@mui/icons-material/Stop';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ComponentType, type ReactElement, type ReactNode, useMemo, useState } from 'react';
import { useI18n } from '../../hooks';
import { iconSizes } from '../../theme/typography';
import { type AstorReasoningStep, type AstorReasoningStepStatus, AstorReasoningTrace } from '../AstorReasoningTrace';
import { ASTOR_TOOL_RENDERERS, getAstorToolRenderer } from '../AstorToolRenderers/registry';
import { ToolResultSkeleton } from '../AstorToolRenderers/ToolResultSkeleton';
import type { AstorToolResult } from '../AstorToolRenderers/types';
import { GlassButton } from '../GlassButton/GlassButton';
import { GlassIconButton } from '../GlassIconButton/GlassIconButton';
import { useAstorRuntime } from './runtimeAdapter';
import type { AstorThreadProps } from './types';

const defaultRenderContent = (content: string): ReactNode => content;

// ---------------------------------------------------------------------------
// Tool rendering — adapt assistant-ui's ToolCallMessagePart shape into
// AstorToolRendererProps. Each registered renderer becomes a component that
// receives { args, argsText, result, toolName, toolCallId, status, isError }.
// ---------------------------------------------------------------------------

interface ToolPartProps {
  toolCallId: string;
  toolName: string;
  args: unknown;
  argsText?: string;
  result?: unknown;
  isError?: boolean;
  status?: { type: string };
}

interface BuildToolMapOptions {
  onNavigate?: (path: string) => void;
}

function buildToolComponentMap({ onNavigate }: BuildToolMapOptions): Record<string, ComponentType<ToolPartProps>> {
  const map: Record<string, ComponentType<ToolPartProps>> = {};
  for (const toolName of Object.keys(ASTOR_TOOL_RENDERERS)) {
    const Renderer = getAstorToolRenderer(toolName);
    if (!Renderer) continue;
    map[toolName] = (part: ToolPartProps) => {
      const isInFlight = part.status?.type === 'running' || part.status?.type === 'requires-action';
      if (isInFlight && part.result === undefined) {
        return <ToolResultSkeleton toolName={part.toolName} />;
      }
      if (part.result === undefined) return null;
      return (
        <Renderer data={part.result} toolCallId={part.toolCallId} toolName={part.toolName} onNavigate={onNavigate} />
      );
    };
  }
  return map;
}

// ---------------------------------------------------------------------------
// Per-part Text renderer — host's renderContent wraps the text content.
// `assistant-ui` passes a `TextMessagePartProps` shape (MessagePartState +
// TextMessagePart) — we only consume `.text`, the rest is ignored.
// ---------------------------------------------------------------------------

interface TextPart {
  text: string;
  // assistant-ui's TextMessagePartProps has more fields (status, type, etc.) —
  // they're irrelevant for renderContent and the cast at the consumption site
  // satisfies the type checker.
}

const buildTextComponent = (renderContent: (content: string) => ReactNode): ComponentType<TextPart> => {
  return ({ text }: TextPart) => <>{renderContent(text)}</>;
};

// ---------------------------------------------------------------------------
// User message — right-aligned compact pill
// ---------------------------------------------------------------------------

interface UserMessageProps {
  TextComponent: ComponentType<TextPart>;
  showEdit: boolean;
}

const UserMessage = ({ TextComponent, showEdit }: UserMessageProps): ReactElement => {
  const { t } = useI18n();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <MessagePrimitive.Root asChild>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          mb: 1,
          gap: 0.5,
          // Hover-reveal the action bar to keep the user pill visually clean.
          '&:hover .astor-user-actions': { opacity: 1 },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            maxWidth: '70%',
            width: 'fit-content',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            color: 'text.primary',
            borderRadius: 3,
            fontSize: '0.9375rem', // body2 / 15pt Apple Callout — matches the rest of the platform
            lineHeight: 1.55,
            '& p': { margin: 0 },
          }}
        >
          {/* biome-ignore lint/suspicious/noExplicitAny: TextComponent satisfies the runtime contract; assistant-ui's TextMessagePartComponent type pulls in MessagePartState we don't need. */}
          <MessagePrimitive.Parts components={{ Text: TextComponent as any }} />
        </Box>

        {showEdit && (
          <ActionBarPrimitive.Root hideWhenRunning autohide="not-last" asChild>
            <Box
              className="astor-user-actions"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                opacity: 0,
                transition: 'opacity 0.15s ease',
                '& button:hover': { color: 'text.primary' },
              }}
            >
              <ActionBarPrimitive.Edit asChild>
                <GlassIconButton size="small" aria-label={t('astorThread.action.edit')}>
                  <EditIcon sx={{ fontSize: iconSizes.md }} />
                </GlassIconButton>
              </ActionBarPrimitive.Edit>
            </Box>
          </ActionBarPrimitive.Root>
        )}
      </Box>
    </MessagePrimitive.Root>
  );
};

// ---------------------------------------------------------------------------
// Reasoning trace — collapsible tool-call summary above the assistant prose.
// Reads toolResults off the assistant-ui MessageState's metadata.custom
// (re-attached there by runtimeAdapter so we can pull durationMs/pending/error
// that ToolCallMessagePart doesn't carry). Renders nothing when no tools fired.
// ---------------------------------------------------------------------------

interface ReasoningTraceBlockProps {
  /** Toggle label translation function — pulled in once so the block is stateless. */
  toggleShowLabel: string;
  toggleHideLabel: string;
  totalDurationLabel: (duration: string) => string;
}

const isAstorToolResultArray = (value: unknown): value is AstorToolResult[] =>
  Array.isArray(value) &&
  value.every((v) => typeof v === 'object' && v !== null && 'toolCallId' in v && 'toolName' in v);

const formatMs = (ms?: number): string | undefined => {
  if (ms == null || Number.isNaN(ms)) return undefined;
  return `${Math.max(0, Math.round(ms))}ms`;
};

const formatTotalDuration = (totalMs: number): string => {
  if (totalMs < 1000) return `${Math.round(totalMs)}ms`;
  return `${(totalMs / 1000).toFixed(1)}s`;
};

const summarizeResult = (tr: AstorToolResult): string | undefined => {
  if (tr.error) return tr.error;
  if (tr.pending) return undefined;
  const { data } = tr;
  if (data == null) return undefined;
  if (typeof data === 'string') return data;
  if (typeof data === 'number' || typeof data === 'boolean') return String(data);
  if (Array.isArray(data)) return `${data.length} result${data.length === 1 ? '' : 's'}`;
  if (typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const items = obj['items'];
    if (Array.isArray(items)) return `${items.length} item${items.length === 1 ? '' : 's'}`;
    const rows = obj['rows'];
    if (Array.isArray(rows)) return `${rows.length} row${rows.length === 1 ? '' : 's'}`;
    const summary = obj['summary'];
    if (typeof summary === 'string') return summary;
    const messageStr = obj['message'];
    if (typeof messageStr === 'string') return messageStr;
  }
  return undefined;
};

const ReasoningTraceBlock = ({
  toggleShowLabel,
  toggleHideLabel,
  totalDurationLabel,
}: ReasoningTraceBlockProps): ReactElement | null => {
  // Default to expanded so the per-tool reasoning rows are visible inline —
  // matches the UX v3 design intent (`AstorActive` in
  // docs/ux-v3-1749/pages/home-astor.jsx lines 506-530, Issue #1749). The
  // toggle still lets users collapse for clean re-reading.
  const [expanded, setExpanded] = useState(true);
  // Pull toolResults off metadata.custom; falls back to [] when none are present.
  const toolResults = useMessage((m) => {
    const custom = m.metadata?.custom as Record<string, unknown> | undefined;
    const raw = custom?.['toolResults'];
    return isAstorToolResultArray(raw) ? raw : undefined;
  });

  if (!toolResults || toolResults.length === 0) return null;

  const steps: AstorReasoningStep[] = toolResults.map((tr) => {
    const status: AstorReasoningStepStatus = tr.error ? 'error' : tr.pending ? 'pending' : 'success';
    return {
      id: tr.toolCallId,
      tool: `${tr.toolName}()`,
      result: summarizeResult(tr),
      ms: formatMs(tr.durationMs),
      status,
    };
  });

  const totalMs = toolResults.reduce((acc, tr) => acc + (tr.durationMs ?? 0), 0);
  const totalDuration = totalMs > 0 ? formatTotalDuration(totalMs) : undefined;

  return (
    <Box sx={{ mb: 1 }}>
      <GlassButton
        variant="text"
        size="small"
        onClick={() => setExpanded((v) => !v)}
        sx={{ mb: expanded ? 0.5 : 0, minWidth: 0, px: 0.75, py: 0.25, textTransform: 'none' }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {expanded ? toggleHideLabel : toggleShowLabel}
          {totalDuration ? ` · ${totalDurationLabel(totalDuration)}` : ''}
        </Typography>
      </GlassButton>
      {expanded && <AstorReasoningTrace dense steps={steps} totalDuration={totalDuration} />}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Assistant message — sparkle icon + content + tool registry + action bar + branch picker
// ---------------------------------------------------------------------------

interface AssistantMessageProps {
  TextComponent: ComponentType<TextPart>;
  toolComponents: Record<string, ComponentType<ToolPartProps>>;
  showActions: boolean;
}

const AssistantMessage = ({ TextComponent, toolComponents, showActions }: AssistantMessageProps): ReactElement => {
  const { t } = useI18n();
  // Pre-resolve toggle labels + total-duration formatter so ReasoningTraceBlock
  // stays free of `useI18n()` (it's used inside the message scope where one
  // hook call is enough).
  const toggleShowLabel = t('astor.chat.toolTrace.toggleShow');
  const toggleHideLabel = t('astor.chat.toolTrace.toggleHide');
  const totalDurationLabel = (duration: string): string => t('astor.chat.toolTrace.totalDuration', { duration });
  return (
    <MessagePrimitive.Root asChild>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          <SparkleIcon sx={{ fontSize: iconSizes.base, color: 'primary.main' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ReasoningTraceBlock
            toggleShowLabel={toggleShowLabel}
            toggleHideLabel={toggleHideLabel}
            totalDurationLabel={totalDurationLabel}
          />
          <Box
            sx={{
              color: 'text.primary',
              fontSize: '0.9375rem', // body2 / 15pt Apple Callout — matches the rest of the platform
              lineHeight: 1.65,
              '& p': { margin: 0 },
              '& p:not(:last-child)': { marginBottom: 1.5 },
            }}
          >
            {/* biome-ignore lint/suspicious/noExplicitAny: see UserMessage for rationale. */}
            <MessagePrimitive.Parts components={{ Text: TextComponent as any, tools: toolComponents as any }} />
            {/* Visible thinking indicator while the agent is running and the
                first content chunk hasn't arrived yet. Without this the user
                sees a blank assistant bubble with just the sparkle icon and
                no progress signal — looks indistinguishable from "stuck". */}
            <MessagePrimitive.If hasContent={false}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  py: 0.5,
                  '& span': {
                    display: 'inline-block',
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'text.secondary',
                    opacity: 0.4,
                    animation: 'astorThinking 1.2s infinite ease-in-out',
                  },
                  '& span:nth-of-type(2)': { animationDelay: '0.15s' },
                  '& span:nth-of-type(3)': { animationDelay: '0.3s' },
                  '@keyframes astorThinking': {
                    '0%, 80%, 100%': { opacity: 0.2, transform: 'translateY(0)' },
                    '40%': { opacity: 1, transform: 'translateY(-2px)' },
                  },
                }}
                aria-label={t('astorThread.empty.thinking', 'Thinking…')}
              >
                <span />
                <span />
                <span />
              </Box>
            </MessagePrimitive.If>
          </Box>

          <BranchPickerPrimitive.Root hideWhenSingleBranch asChild>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.75,
                color: 'text.secondary',
              }}
            >
              <BranchPickerPrimitive.Previous asChild>
                <GlassIconButton size="small" aria-label={t('astorThread.branch.previous')}>
                  <Typography variant="caption">‹</Typography>
                </GlassIconButton>
              </BranchPickerPrimitive.Previous>
              <Typography variant="caption">
                <BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
              </Typography>
              <BranchPickerPrimitive.Next asChild>
                <GlassIconButton size="small" aria-label={t('astorThread.branch.next')}>
                  <Typography variant="caption">›</Typography>
                </GlassIconButton>
              </BranchPickerPrimitive.Next>
            </Box>
          </BranchPickerPrimitive.Root>

          {showActions && (
            <ActionBarPrimitive.Root hideWhenRunning autohide="not-last" asChild>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 0.5,
                  mt: 1.5,
                  color: 'text.secondary',
                  '& button:hover': { color: 'text.primary' },
                }}
              >
                <ActionBarPrimitive.Copy asChild>
                  <GlassIconButton size="small" aria-label={t('astorThread.action.copy')}>
                    <CopyIcon sx={{ fontSize: iconSizes.md }} />
                  </GlassIconButton>
                </ActionBarPrimitive.Copy>
                <ActionBarPrimitive.Reload asChild>
                  <GlassIconButton size="small" aria-label={t('astorThread.action.regenerate')}>
                    <RefreshIcon sx={{ fontSize: iconSizes.md }} />
                  </GlassIconButton>
                </ActionBarPrimitive.Reload>
                <ActionBarPrimitive.FeedbackPositive asChild>
                  <GlassIconButton size="small" aria-label={t('astorThread.action.feedbackPositive')}>
                    <ThumbUpIcon sx={{ fontSize: iconSizes.md }} />
                  </GlassIconButton>
                </ActionBarPrimitive.FeedbackPositive>
                <ActionBarPrimitive.FeedbackNegative asChild>
                  <GlassIconButton size="small" aria-label={t('astorThread.action.feedbackNegative')}>
                    <ThumbDownIcon sx={{ fontSize: iconSizes.md }} />
                  </GlassIconButton>
                </ActionBarPrimitive.FeedbackNegative>
              </Box>
            </ActionBarPrimitive.Root>
          )}
        </Box>
      </Box>
    </MessagePrimitive.Root>
  );
};

// ---------------------------------------------------------------------------
// Composer
// ---------------------------------------------------------------------------

interface ComposerRowProps {
  enableVoice: boolean;
}

const ComposerRow = ({ enableVoice }: ComposerRowProps): ReactElement => {
  const { t } = useI18n();
  return (
    // Letting ComposerPrimitive.Root render its own <form> directly (no asChild)
    // is what makes Enter-to-submit work: assistant-ui's Input handler does
    // textarea.closest('form').requestSubmit(), which only finds an ancestor
    // when the form is a real <form> element. Wrapping with `<Box component="form" asChild>`
    // collapsed to <div> in some Slot-cloning paths.
    <ComposerPrimitive.Root>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, p: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <ComposerPrimitive.Input
          asChild
          placeholder={t('astorThread.composer.placeholder')}
          rows={1}
          aria-label={t('astorThread.composer.placeholder')}
        >
          <Box
            component="textarea"
            sx={{
              flex: 1,
              minHeight: 40,
              maxHeight: 200,
              border: 0,
              outline: 'none',
              resize: 'none',
              bgcolor: 'transparent',
              color: 'text.primary',
              fontFamily: 'inherit',
              fontSize: '0.9375rem', // body2 / 15pt Apple Callout — matches the message bubbles
              lineHeight: 1.55,
              '&::placeholder': { color: 'text.secondary' },
            }}
          />
        </ComposerPrimitive.Input>

        {enableVoice && (
          <ComposerPrimitive.Dictate asChild>
            <GlassIconButton size="small" aria-label={t('astorThread.composer.dictate')}>
              <MicIcon sx={{ fontSize: iconSizes.md }} />
            </GlassIconButton>
          </ComposerPrimitive.Dictate>
        )}

        <ThreadPrimitive.If running={false}>
          <ComposerPrimitive.Send asChild>
            <GlassIconButton size="small" aria-label={t('astorThread.composer.send')}>
              <SendIcon sx={{ fontSize: iconSizes.md }} />
            </GlassIconButton>
          </ComposerPrimitive.Send>
        </ThreadPrimitive.If>

        <ThreadPrimitive.If running>
          <ComposerPrimitive.Cancel asChild>
            <GlassIconButton size="small" aria-label={t('astorThread.composer.cancel')}>
              <StopIcon sx={{ fontSize: iconSizes.md }} />
            </GlassIconButton>
          </ComposerPrimitive.Cancel>
        </ThreadPrimitive.If>
      </Box>
    </ComposerPrimitive.Root>
  );
};

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

const EmptyState = (): ReactElement => {
  const { t } = useI18n();
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <Typography variant="h6" sx={{ color: 'text.secondary' }} gutterBottom>
        {t('astorThread.empty.title')}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('astorThread.empty.subtitle')}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export const AstorThread = ({
  adapter,
  showComposer = true,
  enableVoice = false,
  renderContent = defaultRenderContent,
  emptyState,
}: AstorThreadProps): ReactElement => {
  const runtime = useAstorRuntime(adapter, { enableVoice });

  const TextComponent = useMemo(() => buildTextComponent(renderContent), [renderContent]);
  const toolComponents = useMemo(
    () => buildToolComponentMap({ onNavigate: adapter.onToolNavigate }),
    [adapter.onToolNavigate],
  );

  // Edit and Reload action-bar buttons only make sense when the host adapter
  // exposes the corresponding callbacks. When neither is set, suppress the
  // entire action bar so the buttons don't render with broken clicks.
  const showActions = adapter.forkFromMessage != null || adapter.reloadFromMessage != null;
  // The Edit button on the user pill specifically routes through forkFromMessage.
  const showUserEdit = adapter.forkFromMessage != null;

  const userMessageComponent = useMemo(
    () => () => <UserMessage TextComponent={TextComponent} showEdit={showUserEdit} />,
    [TextComponent, showUserEdit],
  );
  const assistantMessageComponent = useMemo(
    () => () => (
      <AssistantMessage TextComponent={TextComponent} toolComponents={toolComponents} showActions={showActions} />
    ),
    [TextComponent, toolComponents, showActions],
  );

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ThreadPrimitive.Root asChild>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <ThreadPrimitive.Viewport asChild>
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, px: 1, py: 2 }}>
              <ThreadPrimitive.Empty>{emptyState ?? <EmptyState />}</ThreadPrimitive.Empty>
              <ThreadPrimitive.Messages
                components={{
                  UserMessage: userMessageComponent,
                  AssistantMessage: assistantMessageComponent,
                }}
              />
            </Box>
          </ThreadPrimitive.Viewport>
          {showComposer && <ComposerRow enableVoice={enableVoice} />}
        </Box>
      </ThreadPrimitive.Root>
    </AssistantRuntimeProvider>
  );
};

export default AstorThread;
